import { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { API_BASE } from "./utils/apiBase";

const LOGIN_RATE_LIMIT_STORAGE_KEY = "ezra_hcm_login_rate_limit_until";
const LOGIN_RATE_LIMIT_MESSAGE = "Too many failed attempts. Please try again in 5 minutes.";

function Login({ onLoginSuccess }) {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [waitingForOtp, setWaitingForOtp] = useState(false);
  const [otpCooldownSec, setOtpCooldownSec] = useState(0);
  const [loginCooldownSec, setLoginCooldownSec] = useState(0);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (!waitingForOtp || otpCooldownSec <= 0) return undefined;
    const timer = setInterval(() => {
      setOtpCooldownSec((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [waitingForOtp, otpCooldownSec]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const storedUntil = Number(window.sessionStorage.getItem(LOGIN_RATE_LIMIT_STORAGE_KEY) || 0);
    if (!storedUntil || storedUntil <= Date.now()) {
      window.sessionStorage.removeItem(LOGIN_RATE_LIMIT_STORAGE_KEY);
      return undefined;
    }
    setLoginCooldownSec(Math.ceil((storedUntil - Date.now()) / 1000));
    return undefined;
  }, []);

  useEffect(() => {
    if (loginCooldownSec <= 0) return undefined;
    const timer = setInterval(() => {
      setLoginCooldownSec((prev) => {
        if (prev <= 1) {
          if (typeof window !== "undefined") {
            window.sessionStorage.removeItem(LOGIN_RATE_LIMIT_STORAGE_KEY);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [loginCooldownSec]);

  const formatCooldown = (seconds) => {
    const safe = Math.max(0, Number(seconds || 0));
    const mm = String(Math.floor(safe / 60)).padStart(2, "0");
    const ss = String(safe % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const sanitizeUserFacingError = (value, fallback = "") => {
    const raw = String(value || "").trim();
    if (!raw) return fallback;
    const cleaned = raw
      .replace(/\s*\(status\s*\d+\)\s*/gi, " ")
      .replace(/\bstatus\s*\d+\b[:\s-]*/gi, "")
      .replace(/\bhttp\s*\d+\b[:\s-]*/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    return cleaned || fallback;
  };

  const applyLoginCooldown = (seconds) => {
    const safeSeconds = Math.max(0, Number(seconds || 0));
    setLoginCooldownSec(safeSeconds);
    if (typeof window === "undefined") return;
    if (safeSeconds > 0) {
      window.sessionStorage.setItem(
        LOGIN_RATE_LIMIT_STORAGE_KEY,
        String(Date.now() + (safeSeconds * 1000))
      );
      return;
    }
    window.sessionStorage.removeItem(LOGIN_RATE_LIMIT_STORAGE_KEY);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting || loginCooldownSec > 0) return;
    setError("");
    setNotice("");
    setSubmitting(true);

    try {
      const cleaned = String(employeeId || "").replace(/\D/g, "").slice(0, 8);
      if (!cleaned) {
        setError("ID number is required");
        setSubmitting(false);
        return;
      }
      const payload = { employeeid: cleaned.padStart(8, "0"), password };
      const res = await axios.post(
        API_BASE + "/api/login",
        payload,
        { withCredentials: true }
      );
      if (res.data.status === "ok") {
        applyLoginCooldown(0);
        await onLoginSuccess(res.data.user);
      } else if (res.data.status === "must_change_password") {
        applyLoginCooldown(0);
        await onLoginSuccess(res.data.user);
      } else if (res.data.status === "otp_sent") {
        applyLoginCooldown(0);
        setWaitingForOtp(true);
        setOtpCooldownSec(Number(res?.data?.resend_in_seconds) || 60);
        if (res?.data?.dev_otp) {
          setNotice(`Development OTP mode is active. Use OTP: ${res.data.dev_otp}`);
        } else if (res?.data?.message) {
          setNotice(res.data.message);
        } else {
          setNotice("OTP sent. Check your email.");
        }
      }
    } catch (err) {
      const rateLimitCode = err?.response?.data?.code;
      const retryAfter = Number(err?.response?.data?.retry_after_seconds || 0);
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
      const status = err?.response?.status;
      if (status === 429 || rateLimitCode === "LOGIN_RATE_LIMITED") {
        applyLoginCooldown(retryAfter > 0 ? retryAfter : 300);
        setError("");
      } else if (status === 401 || serverMsg === "Invalid credentials") {
        setError("Invalid employee ID or password");
      } else if (!status || status >= 500) {
        setError("Login failed. Please try again.");
      } else if (serverMsg) {
        setError(sanitizeUserFacingError(serverMsg, "Login failed. Please try again."));
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpCooldownSec > 0 || resendingOtp) return;
    setError("");
    setNotice("");
    setResendingOtp(true);
    try {
      const res = await axios.post(
        API_BASE + "/api/resend-otp",
        {},
        { withCredentials: true }
      );
      if (res?.data?.status === "otp_resent") {
        setOtpCooldownSec(Number(res?.data?.resend_in_seconds) || 60);
        if (res?.data?.dev_otp) {
          setNotice(`Development OTP mode is active. Use OTP: ${res.data.dev_otp}`);
        } else if (res?.data?.message) {
          setNotice(res.data.message);
        } else {
          setNotice("OTP resent. Check your email.");
        }
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.error;
      const retryAfter = Number(err?.response?.data?.retry_after_seconds || 0);
      if (retryAfter > 0) setOtpCooldownSec(retryAfter);
      setError(sanitizeUserFacingError(serverMsg, "Failed to resend OTP."));
    } finally {
      setResendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");

    try {
      const res = await axios.post(
        API_BASE + "/api/verify-otp",
        { otp },
        { withCredentials: true }
      );
      if (res.data.status === "ok") {
        await onLoginSuccess(res.data.user);
      } else if (res.data.status === "must_change_password") {
        await onLoginSuccess(res.data.user);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.error;
      setError(sanitizeUserFacingError(serverMsg, "OTP verification failed"));
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f3f4f6",
        fontFamily: "Roboto, sans-serif",
        px: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 380, borderRadius: 4, boxShadow: 4 }}>
        <CardContent sx={{ position: "relative" }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{ position: "absolute", top: 8, left: 8 }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
            <Avatar sx={{ bgcolor: "#2563eb", mb: 1 }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography variant="h5" fontWeight={500}>
              Ezra HCM Login
            </Typography>
          </Box>

          {!waitingForOtp ? (
            <form onSubmit={handleLogin}>
              {loginCooldownSec > 0 && (
                <Typography color="error" variant="body2" align="center" mb={1}>
                  {`${LOGIN_RATE_LIMIT_MESSAGE} Try again in ${formatCooldown(loginCooldownSec)}.`}
                </Typography>
              )}
              {error && (
                <Typography color="error" variant="body2" align="center" mb={1}>
                  {error}
                </Typography>
              )}
              {notice && (
                <Typography color="primary" variant="body2" align="center" mb={1}>
                  {notice}
                </Typography>
              )}

              <TextField
                label="ID number"
                fullWidth
                margin="normal"
                value={employeeId}
                onChange={(e) => setEmployeeId(String(e.target.value || "").replace(/\D/g, "").slice(0, 8))}
                required
                inputProps={{ autoComplete: "username", inputMode: "numeric", pattern: "[0-9]*", maxLength: 8 }}
              />

              <TextField
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                inputProps={{ autoComplete: "current-password" }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={submitting || loginCooldownSec > 0}
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  py: 1.2,
                  backgroundColor: "#2563eb",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "#1e40af" },
                }}
              >
                {submitting
                  ? "Logging in..."
                  : (loginCooldownSec > 0 ? `Try again in ${formatCooldown(loginCooldownSec)}` : "Login")}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp}>
              <Typography variant="h6" align="center" mb={2}>
                Enter OTP
              </Typography>
              {error && (
                <Typography color="error" variant="body2" align="center" mb={1}>
                  {error}
                </Typography>
              )}
              {notice && (
                <Typography color="primary" variant="body2" align="center" mb={1}>
                  {notice}
                </Typography>
              )}

              <TextField
                label="6-digit OTP"
                fullWidth
                margin="normal"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  borderRadius: 3,
                  py: 1.2,
                  backgroundColor: "#2563eb",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": { backgroundColor: "#1e40af" },
                }}
              >
                Verify OTP
              </Button>

              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={handleResendOtp}
                disabled={resendingOtp || otpCooldownSec > 0}
                sx={{
                  mt: 1.5,
                  borderRadius: 3,
                  py: 1.1,
                  textTransform: "none",
                }}
              >
                {otpCooldownSec > 0
                  ? `Resend OTP in ${formatCooldown(otpCooldownSec)}`
                  : (resendingOtp ? "Resending..." : "Resend OTP")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Login;
